// src/componentes/AdDeuda/EditarDeuda.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp, getDocs, collection } from 'firebase/firestore';
import { db } from '../../Firebase/firebaseconfig';
import notify from "../common/notify";
import { parseMoney, parseInteger } from "../../helpers/parseNumeric";
import './AgregarDeuda.css'; // reutiliza estilos de form
import { useUserContext } from '../GestionDeUsuarios/UserContext';
import AccessDenied from '../Auth/AccessDenied';
import { registrarAuditoria } from '../../helpers/auditoria';

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const timestampToInputDate = (value) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return '';
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const EditarDeuda = () => {
  const { id: clienteId, deudaId } = useParams();
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [form, setForm] = useState({
    concepto: '',
    monto: '',
    enganche: '',
    mensualidad: '',
    plazo: '',
    fecha: '',
    referencia: '',
    fueraPais: false,
    responsableId: ''
  });

  const [deudaOriginal, setDeudaOriginal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const snap = await getDoc(doc(db, 'deudas', deudaId));

        if (snap.exists()) {
          const data = snap.data();
          setDeudaOriginal(data);

          setForm({
            concepto: data.concepto || '',
            monto: (data.monto ?? data.importe ?? '').toString(),
            enganche: (data.enganche ?? '').toString(),
            mensualidad: (data.mensualidad ?? '').toString(),
            plazo: (data.plazo ?? '').toString(),
            fecha: timestampToInputDate(
              data.fechaInicio ||
              data.fechaInicioDeuda ||
              data.fechaContrato ||
              data.fechaVenta ||
              data.lastPaymentDate
            ),
            referencia: data.referencia || '',
            fueraPais: data.fueraPaisRegularmente || false,
            responsableId: data.responsableId || ''
          });

          setSearchTerm(data.responsableId || '');
        }

        const snapCl = await getDocs(collection(db, 'clientes'));
        setClientes(snapCl.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error cargando datos:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [deudaId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'fueraPais') {
      if (!checked) {
        setForm(prev => ({
          ...prev,
          fueraPais: false,
          responsableId: ''
        }));
        setSearchTerm('');
      } else {
        setForm(prev => ({
          ...prev,
          fueraPais: true
        }));
      }
      return;
    }

    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setForm(prev => ({ ...prev, responsableId: '' }));
  };

  const selectCliente = (c) => {
    const nombreCompleto = [c.nombre, c.apellidos].filter(Boolean).join(' ');
    setForm(prev => ({ ...prev, responsableId: c.id }));
    setSearchTerm(nombreCompleto || c.nombre || c.id);
  };

  const filtered = clientes
    .filter(c => {
      const nombre = `${c.nombre || ''} ${c.apellidos || ''}`.toLowerCase();
      const id = (c.id || '').toLowerCase();
      const term = searchTerm.toLowerCase();
      return nombre.includes(term) || id.includes(term);
    })
    .slice(0, 10);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!can('deudas.editar')) return;
    if (isSubmitting) return;

    const campos = ['concepto', 'monto', 'enganche', 'mensualidad', 'plazo', 'fecha'];
    for (const campo of campos) {
      if (!form[campo]) {
        notify.error('Todos los campos obligatorios deben estar llenos.');
        return;
      }
    }

    const montoP = parseMoney(form.monto);
    const enganceP = parseMoney(form.enganche);
    const mensualP = parseMoney(form.mensualidad);
    const plazoP = parseInteger(form.plazo, { min: 0 });
    const [year, month, day] = form.fecha.split("-").map(Number);
    const fechaDate = new Date(year, month - 1, day);
    fechaDate.setHours(0, 0, 0, 0);

    if (!montoP.valid || !enganceP.valid || !mensualP.valid || !plazoP.valid) {
      notify.error('Monto, enganche, mensualidad y plazo deben ser números positivos válidos.');
      return;
    }
    if (montoP.value <= 0) {
      notify.error('El monto debe ser mayor a cero.');
      return;
    }
    if (isNaN(fechaDate.getTime())) {
      notify.error('La fecha no es válida.');
      return;
    }
    const monto = montoP.value;
    const enganche = enganceP.value;
    const mensualidad = mensualP.value;
    const plazo = plazoP.value;

    if (form.fueraPais && !form.responsableId) {
      notify.error('Debes seleccionar un responsable.');
      return;
    }

    setIsSubmitting(true);

    try {
      fechaDate.setHours(0, 0, 0, 0);

const fechaInicio = Timestamp.fromDate(fechaDate);
const lastPaymentDate = Timestamp.fromDate(fechaDate);

const next = new Date(fechaDate);
next.setMonth(next.getMonth() + 1);
      const pagos = Array.isArray(deudaOriginal?.pagos) ? deudaOriginal.pagos : [];

      let totalPagadoPositivo = 0;
      let hayEngancheEnPagos = false;

      for (const p of pagos) {
        const cantidad = toNumber(p?.cantidad);
        const tipo = String(p?.tipo || '').toLowerCase();

        if (cantidad > 0) totalPagadoPositivo += cantidad;
        if (tipo === 'enganche' && cantidad > 0) hayEngancheEnPagos = true;
      }

      const totalCubierto = hayEngancheEnPagos
        ? totalPagadoPositivo
        : totalPagadoPositivo + enganche;

      const nuevoSaldoRestante = Math.max(monto - totalCubierto, 0);

      // ¿Cambió la fecha de inicio respecto a la original? Solo entonces
      // reanclamos las fechas derivadas. Comparamos contra el mismo campo con
      // que se prellenó el input (timestampToInputDate + misma precedencia).
      const fechaOriginalStr = timestampToInputDate(
        deudaOriginal?.fechaInicio ||
          deudaOriginal?.fechaInicioDeuda ||
          deudaOriginal?.fechaContrato ||
          deudaOriginal?.fechaVenta ||
          deudaOriginal?.lastPaymentDate
      );
      const fechaCambio = form.fecha !== fechaOriginalStr;

      const updates = {
        concepto: form.concepto,
        monto,
        importe: monto,
        enganche,
        mensualidad,
        plazo,
        referencia: form.referencia,
        fueraPaisRegularmente: form.fueraPais,
        responsableId: form.fueraPais ? form.responsableId : null,

        saldo: nuevoSaldoRestante,
        saldoRestante: nuevoSaldoRestante,
      };

      // Reanclar fechas SOLO si el usuario cambió la fecha de inicio. Así,
      // editar concepto/monto/etc. NO toca nextDueDate/lastPaymentDate de una
      // deuda con historial (evita colapsar el plan/mora al mes 1). Al cambiar
      // la fecha sí se reancla: nextDueDate = inicio + 1 mes (ancla del motor
      // V2), o null si quedó liquidada.
      if (fechaCambio) {
        updates.fechaInicio = fechaInicio;
        updates.fechaInicioDeuda = fechaInicio;
        updates.fechaContrato = fechaInicio;
        updates.lastPaymentDate = lastPaymentDate;
        updates.nextDueDate =
          nuevoSaldoRestante > 0 ? Timestamp.fromDate(next) : null;
      }

      await updateDoc(doc(db, 'deudas', deudaId), updates);
      await registrarAuditoria({
        accion: 'deudas.editar',
        entidadTipo: 'deuda',
        entidadId: deudaId,
        antes: deudaOriginal,
        despues: updates,
      });
      navigate(`/app/cliente/${clienteId}/deudas/${deudaId}`);
    } catch (err) {
      console.error('Error al guardar cambios:', err);
      notify.error('No se pudieron guardar los cambios.');
      setIsSubmitting(false);
    }
  };

  if (loading) return <div>Cargando datos para editar…</div>;

  if (!can('deudas.editar')) {
    return <AccessDenied message="No tienes permiso para editar deudas." />;
  }

  return (
    <div className="agregar-deuda-container">
      <h2>Editar Deuda</h2>

      <form onSubmit={handleSubmit} className="form-deuda">
        <input
          type="text"
          name="concepto"
          placeholder="Concepto"
          value={form.concepto}
          onChange={handleChange}
          required
        />

        <input
          type="number"
          name="monto"
          placeholder="Monto total"
          value={form.monto}
          onChange={handleChange}
          required
        />

        <input
          type="number"
          name="enganche"
          placeholder="Enganche"
          value={form.enganche}
          onChange={handleChange}
          required
        />

        <input
          type="number"
          name="mensualidad"
          placeholder="Mensualidad pactada"
          value={form.mensualidad}
          onChange={handleChange}
          required
        />

        <input
          type="number"
          name="plazo"
          placeholder="Plazo en meses"
          value={form.plazo}
          onChange={handleChange}
          required
        />

        <label>
          Fecha de Inicio:
          <input
            type="date"
            name="fecha"
            value={form.fecha}
            onChange={handleChange}
            required
          />
        </label>

        <input
          type="text"
          name="referencia"
          placeholder="Referencia"
          value={form.referencia}
          onChange={handleChange}
        />

        <label>
          <input
            type="checkbox"
            name="fueraPais"
            checked={form.fueraPais}
            onChange={handleChange}
          />{' '}
          Cliente fuera del país con regularidad
        </label>

        {form.fueraPais && (
          <div className="search-responsable">
            <input
              type="text"
              placeholder="Buscar responsable..."
              value={searchTerm}
              onChange={handleSearchChange}
              autoComplete="off"
            />

            {searchTerm && !form.responsableId && (
              <ul className="results-list">
                {filtered.map(c => {
                  const nombreCompleto = [c.nombre, c.apellidos].filter(Boolean).join(' ');
                  return (
                    <li key={c.id} onClick={() => selectCliente(c)}>
                      {nombreCompleto || c.nombre} ({c.id})
                    </li>
                  );
                })}
              </ul>
            )}

            {form.responsableId && (
              <div className="selected-responsable">
                Seleccionado: {searchTerm}
              </div>
            )}
          </div>
        )}

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </form>

      <button
        className="volver-btn"
        onClick={() => navigate(`/app/cliente/${clienteId}/deudas/${deudaId}`)}
      >
        Cancelar
      </button>
    </div>
  );
};

export default EditarDeuda;
